import _ from 'lodash';
import OpenAI from 'openai';
import { analysisPrompt, chatSystemPrompt, classifyIntent, type Intent } from 'constants/openAiPrompts';

export class OpenAiApi {
    public openai: OpenAI;
    public assistantId!: string;
    private analysisContext?: string;
    private vectorStoreId?: string;
    private hasFileSearchTool = false;

    constructor(apiKey: string) {
        this.openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
    }

    /** Assistant 초기화 */
    async initAssistant(aiModel: string, vectorStoreId?: string) {
        this.vectorStoreId = vectorStoreId;
        const useFileSearch = Boolean(vectorStoreId);

        const assistant = await this.openai.beta.assistants.create({
            name: 'Psychology Expert',
            model: aiModel,
            temperature: 0.2,
            instructions: `
당신은 아동/청소년 심리검사 결과를 해석하는 전문가입니다.
JSON 생성 시에는 제공된 템플릿과 규칙을 엄격히 따르세요.
채팅 단계에서는 진단을 대체하지 않는 대신 '결정형 답변 포맷'을 우선합니다.
`,
            tools: useFileSearch ? [{ type: 'file_search' }] : [],
            ...(useFileSearch && {
                tool_resources: { file_search: { vector_store_ids: [vectorStoreId!] } },
            }),
        });

        this.assistantId = assistant.id;
        this.hasFileSearchTool = useFileSearch;
    }

    /** PSY_ITEM_ID 추출 */
    private extractPsyItemIds(testRawData: string): string[] {
        const re = /PSY_ITEM_ID["\s]*:[\s]*["']([^"']+)["']/g;
        const ids: string[] = [];
        let m: RegExpExecArray | null;
        while ((m = re.exec(testRawData)) !== null) ids.push(m[1]);
        return _.uniq(ids);
    }

    /** 결과 검증 */
    private validateResult(result: string, expectedCount: number): boolean {
        try {
            const parsed = JSON.parse(result);
            return Array.isArray(parsed.summaryList) && parsed.summaryList.length === expectedCount;
        } catch {
            return false;
        }
    }

    /** Run 완료 대기 + 응답*/
    private async waitForRunAndGetReply(threadId: string, runId: string): Promise<string> {
        let attempts = 0;
        const maxAttempts = 60;

        while (attempts < maxAttempts) {
            const run = await this.openai.beta.threads.runs.retrieve(runId, { thread_id: threadId });

            if (run.status === 'requires_action') {
                await this.openai.beta.threads.runs.submitToolOutputs(runId, {
                    thread_id: threadId,
                    tool_outputs: [],
                });
            } else if (run.status === 'completed') {
                const msgs = await this.openai.beta.threads.messages.list(threadId, { limit: 20 });
                const reply = msgs.data.find((m: any) => m.run_id === runId && m.role === 'assistant');
                if (!reply) throw new Error('Assistant reply not found for this run.');
                const text = (reply.content || [])
                    .map((c: any) => c.text?.value ?? '')
                    .join('\n')
                    .trim();

                console.log('🟢 사용된 토큰:', {
                    prompt: run.usage?.prompt_tokens,
                    completion: run.usage?.completion_tokens,
                    total: run.usage?.total_tokens,
                });
                return text;
            } else if (['failed', 'cancelled', 'expired'].includes(run.status as string)) {
                throw new Error(`Run ended with status: ${run.status}`);
            }

            await new Promise((r) => setTimeout(r, 1000));
            attempts++;
        }
        throw new Error('Run timed out');
    }

    /** 분석 실행: raw data -> JSON */
    async responseAiResult(testRawData: string, opts: { useVectorStore?: boolean } = {}): Promise<string> {
        const psyItemIds = this.extractPsyItemIds(testRawData);
        const thread = await this.openai.beta.threads.create();

        const enhancedPrompt = `
**발견된 PSY_ITEM_ID 개수: ${psyItemIds.length}개**
**PSY_ITEM_ID 목록: ${psyItemIds.join(', ')}

위 정보를 바탕으로 summaryList를 정확히 ${psyItemIds.length}개 생성하세요.**

${testRawData}

${analysisPrompt}
`;

        await this.openai.beta.threads.messages.create(thread.id, {
            role: 'user',
            content: enhancedPrompt,
        });

        const useVector = Boolean(opts.useVectorStore && this.hasFileSearchTool && this.vectorStoreId);

        const run = await this.openai.beta.threads.runs.create(thread.id, {
            assistant_id: this.assistantId,
            tool_choice: useVector ? 'auto' : 'none',
            ...(useVector && {
                tool_resources: { file_search: { vector_store_ids: [this.vectorStoreId!] } },
            }),
        });

        const content = await this.waitForRunAndGetReply(thread.id, run.id);

        const isValid = this.validateResult(content, psyItemIds.length);
        if (!isValid) console.warn(`⚠️ 검증 실패: 예상 ${psyItemIds.length}개, 실제 결과 확인 필요`);

        this.analysisContext = content; // 이후 채팅에서 활용
        return content;
    }

    /** 채팅 Thread 생성 */
    async createChatThread(analysisResult?: string) {
        this.analysisContext = analysisResult ?? this.analysisContext;
        const thread = await this.openai.beta.threads.create();
        return thread;
    }

    /** 일반 Thread 생성 */
    async createThread() {
        return await this.openai.beta.threads.create();
    }

    /** 채팅 질의 */
    async sendChatMessage(testRawData: string, threadId: string, message: string): Promise<string> {
        const intent = classifyIntent(message);

        // 명백한 오프토픽만 거절
        if (intent === 'offtopic') {
            return `죄송해요. 이 화면에서는 **검사 해석/상황 코칭**에 관한 내용만 도와드려요.
예) "K-CDI 우울 점수가 높을 때 집에서 뭘 해보면 좋을까요?", "컴퓨터 과몰입이 계속되는데 우리 집에서는 어떻게 시작하면 좋을까요?"`;
        }

        // 사용자 메시지 안에 컨텍스트를 동봉 (시스템은 가이드만)
        const userContent = this.analysisContext
            ? `의도: ${intent}
질문: ${message}

[analysisContext]
${this.analysisContext}

[testRawData]
${testRawData}`
            : `의도: ${intent}
질문: ${message}`;

        await this.openai.beta.threads.messages.create(threadId, { role: 'user', content: userContent });

        const run = await this.openai.beta.threads.runs.create(threadId, {
            assistant_id: this.assistantId,
            tool_choice: 'none', // 대화는 검색 끔(일관성)
            additional_instructions: chatSystemPrompt,
        });

        const content = await this.waitForRunAndGetReply(threadId, run.id);
        return content || '응답을 받을 수 없습니다.';
    }
}
